import { Injectable } from '@angular/core';
import { distinctUntilArrayChanged, distinctUntilDeepChanged } from '@examdojo/core/rxjs';
import { QueryEntity } from '@examdojo/state';
import { selectActiveId } from '@ngneat/elf-entities';
import { combineLatest, distinctUntilChanged, map, Observable } from 'rxjs';
import { ClassStore } from '../class/class.store';
import { getAssignmentPhase, isAssignmentDue } from './class-assignment-permissions';
import { ClassAssignmentStoreModel, ClassAssignmentUIModel, ClassFilterOption } from './class-assignment.model';
import { ClassAssignmentState, ClassAssignmentStore } from './class-assignment.store';

@Injectable({ providedIn: 'root' })
export class ClassAssignmentQuery extends QueryEntity<
  ClassAssignmentState,
  ClassAssignmentStoreModel,
  ClassAssignmentUIModel,
  'id'
> {
  constructor(
    protected override readonly store: ClassAssignmentStore,
    private readonly classStore: ClassStore,
  ) {
    super(store);
  }

  readonly availableClasses$ = this.entities$.pipe(
    map((entities) =>
      Object.values(
        entities
          .filter((entity) => entity.class_id && entity.class_name)
          .reduce(
            (acc, entity) => ({
              ...acc,
              [entity.class_id]: {
                id: entity.class_id,
                name: entity.class_name,
                emoji: entity.class_emoji,
                examFirstDate: entity.class_exam_first_date,
                archivedAt: entity.class_archived_at,
              },
            }),
            {} as Record<string, ClassFilterOption>,
          ),
      ),
    ),
    distinctUntilDeepChanged(),
  );

  readonly scopedEntities$: Observable<ClassAssignmentUIModel[]> = combineLatest([
    this.entities$,
    this.classStore.pipe(
      selectActiveId(),
      map((id) => id ?? null),
      distinctUntilChanged(),
    ),
  ]).pipe(
    map(([entities, activeId]) => (activeId ? entities.filter((e) => e.class_id === activeId) : entities)),
    distinctUntilArrayChanged(),
  );

  selectHasAssignmentsForClass(classId: string): Observable<boolean> {
    return this.entities$.pipe(
      map((entities) => entities.some((entity) => entity.class_id === classId)),
      distinctUntilChanged(),
    );
  }

  getWeeklyUsedMarks(classId: string, weekStart: Date, weekEnd: Date, excludeAssignmentId?: string): number {
    const matching = this.getAllEntities().filter(
      (entity) =>
        entity.class_id === classId &&
        entity.scheduled_at &&
        new Date(entity.scheduled_at) >= weekStart &&
        new Date(entity.scheduled_at) < weekEnd &&
        entity.assignment_id !== excludeAssignmentId,
    );

    return matching.reduce((sum, entity) => sum + entity.total_marks, 0);
  }

  override readonly toUIModelFn = (entity: ClassAssignmentStoreModel): ClassAssignmentUIModel => {
    const notStartedCount = Math.max(0, entity.total_students - entity.completed_count - entity.in_progress_count);
    const completedPercent =
      entity.total_students > 0 ? Math.round((entity.completed_count / entity.total_students) * 100) : 0;
    const inProgressPercent =
      entity.total_students > 0 ? Math.round((entity.in_progress_count / entity.total_students) * 100) : 0;
    const notStartedPercent =
      entity.total_students > 0 ? Math.round((notStartedCount / entity.total_students) * 100) : 0;

    const permissionContext = {
      ...entity,
      isDue: isAssignmentDue(entity),
    };
    const phase = getAssignmentPhase(permissionContext);

    return {
      ...entity,
      notStartedCount,
      completedPercent,
      inProgressPercent,
      notStartedPercent,
      phase,
      isDue: permissionContext.isDue,
    };
  };
}
